import { useMemo, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { z } from 'zod';

import { ScheduledArchivationAlert } from '@/components/scheduled-archivation-alert';
import { Form } from '@/components/ui/form';
import { ConfirmationModal } from '@/modules/common/components/modals/confirmation-modal';
import { DatePicker } from '@/modules/common/components/molecules/date/date-picker';
import { invalidateTeamMemberWithRelatedQueries } from '@/modules/common/utils/query-invalidation';
import { getAvailableUnarchivationDate, unarchiveTeamMember } from '@/modules/team/api/team.api';
import { TeamMember } from '@/modules/team/types/team-member';
import { formatDisplayDate, parseCalendarDate } from '@/utils/date-formatters';

interface UnarchiveTeamMemberModalProps {
  teamMember: TeamMember;
  onSuccess?: () => void;
  trigger?: React.ReactNode;
  open?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}

const unarchiveSchema = z.object({
  unarchiveDate: z.date({
    required_error: 'authenticated.team:unarchive.unarchiveDateRequired',
  }),
});

type UnarchiveFormData = z.infer<typeof unarchiveSchema>;

/**
 * A comprehensive team member unarchiving modal with date validation and form integration.
 * Provides controlled unarchiving workflow with proper date constraints and user feedback.
 *
 * Mirrors the archive modal functionality but for restoring archived team members.
 */
export function UnarchiveTeamMemberModal({
  teamMember,
  onSuccess,
  trigger,
  open,
  onOpen,
  onClose,
}: UnarchiveTeamMemberModalProps) {
  const { t } = useTranslation(['authenticated.team', 'common']);
  const queryClient = useQueryClient();

  const form = useForm<UnarchiveFormData>({
    resolver: zodResolver(unarchiveSchema),
    mode: 'onChange',
    defaultValues: {
      unarchiveDate: undefined,
    },
  });

  const unarchiveTeamMemberMutation = useMutation({
    mutationFn: ({ memberId, unarchiveDate }: { memberId: string; unarchiveDate: Date }) =>
      unarchiveTeamMember({ memberId, unarchiveDate }),
    onSuccess: async () => {
      await invalidateTeamMemberWithRelatedQueries(queryClient, teamMember.id);
      form.reset();
      onSuccess?.();
    },
  });

  const [isModalOpen, setIsModalOpen] = useState(false);

  // Determine the actual modal open state (external or internal)
  const actualModalOpen = open !== undefined ? open : isModalOpen;

  // Fetch available unarchivation date for unarchive date validation
  const {
    data: availableUnarchivationDate,
    isLoading: isLoadingAvailableDate,
    error: availableDateError,
  } = useQuery({
    queryKey: ['available-unarchivation-date', teamMember.id],
    queryFn: () => getAvailableUnarchivationDate(teamMember.id),
    enabled: actualModalOpen,
  });

  // Determine effective available unarchivation date based on loading/error states
  const effectiveAvailableUnarchivationDate = useMemo(() => {
    if (availableDateError) {
      // If there's an error, default to today
      return new Date();
    }
    return availableUnarchivationDate
      ? parseCalendarDate(availableUnarchivationDate)
      : availableUnarchivationDate;
  }, [availableUnarchivationDate, availableDateError]);

  const handleUnarchive = async (closeModal: () => void) => {
    const isValid = await form.trigger();
    if (!isValid) {
      return;
    }

    const formData = form.getValues();

    try {
      await unarchiveTeamMemberMutation.mutateAsync({
        memberId: teamMember.id,
        unarchiveDate: formData.unarchiveDate,
      });
      closeModal();
    } catch (error) {
      // Error handled by mutation, keep modal open for retry
    }
  };

  // Function to disable dates before the available unarchivation date
  const isDateDisabled = (date: Date): boolean => {
    if (!effectiveAvailableUnarchivationDate) return false;

    // Disable dates that are before the available unarchivation date
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const availableOnly = new Date(
      effectiveAvailableUnarchivationDate.getFullYear(),
      effectiveAvailableUnarchivationDate.getMonth(),
      effectiveAvailableUnarchivationDate.getDate()
    );

    return dateOnly < availableOnly;
  };

  const unarchiveDate = form.watch('unarchiveDate');
  const isFormValid = !!unarchiveDate;

  const messageContent = (
    <div className="space-y-4">
      {teamMember.nextUnarchiveDate && (
        <ScheduledArchivationAlert
          message={t('authenticated.team:scheduledArchive.willBeUnarchivedOn', {
            date: formatDisplayDate(teamMember.nextUnarchiveDate),
          })}
          className="mb-0"
        />
      )}
      <div>
        <Trans
          i18nKey="authenticated.team:unarchive.unarchiveTeamMemberMessage"
          values={{ name: teamMember.name }}
          components={{ strong: <strong /> }}
        />
      </div>
      <div className="space-y-2">
        <p className="font-semibold">{t('authenticated.team:unarchive.whatHappens')}</p>
        <ul className="ml-2 list-inside list-disc space-y-1">
          <li>{t('authenticated.team:unarchive.memberWillBeVisible')}</li>
          <li>{t('authenticated.team:unarchive.canReceiveAssignments')}</li>
          <li>{t('authenticated.team:unarchive.allDataRestored')}</li>
          <li>{t('authenticated.team:unarchive.trackingResumes')}</li>
        </ul>
      </div>
    </div>
  );

  return (
    <ConfirmationModal
      trigger={trigger}
      title={t('authenticated.team:unarchive.unarchiveTeamMember')}
      message={messageContent}
      confirmText={
        unarchiveTeamMemberMutation.isPending
          ? t('authenticated.team:unarchive.unarchiving')
          : t('authenticated.team:unarchive.unarchiveTeamMember')
      }
      confirmVariant="default"
      onConfirm={handleUnarchive}
      isLoading={unarchiveTeamMemberMutation.isPending}
      disabled={!isFormValid || isLoadingAvailableDate}
      open={actualModalOpen}
      onOpen={onOpen || (() => setIsModalOpen(true))}
      onClose={
        onClose ||
        (() => {
          setIsModalOpen(false);
          form.reset();
        })
      }
    >
      <Form {...form}>
        <form className="space-y-4">
          <DatePicker
            name="unarchiveDate"
            control={form.control}
            label={t('authenticated.team:unarchive.unarchiveDate')}
            dateFormat="D MMM, YYYY"
            disabled={isDateDisabled}
            inputDisabled={isLoadingAvailableDate}
            isLoading={isLoadingAvailableDate}
          />
        </form>
      </Form>
    </ConfirmationModal>
  );
}
